import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import {
  type Joint,
  type JointValue,
  type ModelMesh,
  mimicOrder,
} from "@/shared/state/visualization/schema/v1/three-d";
import type { TopicData } from "@/shared/state/visualization/schema/v1/topic-data";

import {
  FileSelector,
  PathSelector,
} from "../../../controls/DataSourceSelector";
import { isViewableAsPlot } from "../../../Panel/predicates";
import { useDataFiles, useFileIdToTopics } from "../../../WorkspaceCtx/hooks";
import { ConfigItem } from "../ConfigItem";

import styles from "./EntityConfig.module.css";
import { jointControlState, limitsLabel } from "./jointControl";
import {
  jointConstantFor,
  jointScaleFor,
  jointValueNote,
  jointValueText,
} from "./jointValueInput";

/**
 * Whether what is in the field is a pose rather than a path.
 *
 * Hoisted so its identity is stable across renders — it is asked on every one,
 * to decide whether the field is showing an error or a value.
 */
function isConstant(text: string): boolean {
  return jointConstantFor(text) !== undefined;
}

interface JointBindingsProps {
  /** The model's joints, as the import recorded them on the entity's mesh. */
  joints: Joint[];
  /**
   * The entity that owns the model, and the mesh these joints came off.
   *
   * Taken as props rather than found by scanning `panelState.data.entities` for
   * an entity whose `mesh.joints` is *referentially* this array. That scan was
   * only ever a workaround for `MeshConfig` being unavailable to edit, and it
   * had a silent failure mode: `zod`'s `.parse()` rebuilds arrays, so a
   * workspace restored from a saved layout would hand this component a joints
   * array that is value-identical and reference-different, and every row would
   * render read-only with nothing logged. The caller already knows both of
   * these, and has already narrowed the mesh to a `model`.
   */
  entityId: string;
  mesh: ModelMesh;
  panelId: string;
}

/**
 * A binding control per joint of a model, for the joints that can take one.
 *
 * Lives on the entity that owns the model, which is the only entity a model has:
 * the file's nodes are drawn by one component, and a joint moves nodes inside
 * it. A robot's joints are configured together or not at all, which is what this
 * panel is for.
 *
 * ## Only the joints worth binding
 *
 * A robot description is mostly joints nothing can drive — fixed attachments,
 * coupled followers, joints pinned by their own limits — and a real one has
 * dozens. Listing each with the reason it offers no control is a fact about the
 * model that never changes, restated on every robot forever, so those rows are
 * omitted and the heading counts what is shown.
 *
 * The ingest wizard's joint list keeps them on purpose: there someone is
 * inspecting a freshly converted robot, and *why* a joint is inert is the thing
 * they are looking at.
 *
 * ## Except when one is still driving the model
 *
 * `poseModelJoints` writes any `jointValues` entry naming a joint in the model's
 * `mimicOrder` that declares an axis — it never asks whether this panel would
 * offer a control for that joint. A URDF that declares only effort and velocity
 * limits reads as pinned under URDF 1.0's defaults, so an import can write a
 * value for such a joint before any row exists.
 *
 * A joint pinned by its limits therefore really is being driven when it carries
 * a value. What it does with that value depends on where the value came from:
 * recorded data poses it as recorded, and an authored constant is held inside
 * the travel its limits leave it, which for a pinned joint is one value. Either
 * way it is bound, saved, and the thing to change if the binding was a mistake.
 *
 * Hiding such a row would make a live binding invisible as well as uneditable.
 * So a joint with a value is listed whatever its state, saying what it is
 * holding. The value itself is left exactly as saved: this form deletes nothing
 * on the user's behalf.
 *
 * Each row writes `mesh.jointValues[joint.name]` — keyed by the joint's own
 * name, the same key `joints` and a `mimic` reference use, so what the sidebar
 * shows and what the renderer samples cannot drift apart.
 *
 * One `FileSelector` scopes every row, following the rest of this panel rather
 * than `DataSourceSelector`, which pairs one file to one path — a robot's joints
 * come from one log, and picking it a dozen times would be absurd.
 *
 * ## A constant is typed into the same field as a path
 *
 * `jointValueSchema` has always taken `TopicData | number`, and the renderer has
 * always resolved the number with no fetch at all. Only the UI could not say it.
 * It is said in the field that is already there rather than in a control beside
 * it: a joint has one value however it was decided, so a second input would be a
 * second place for the two to disagree, and the choice stays visible in the value
 * instead of behind a mode. The path is resolved first — `0.5` is syntactically
 * also "topic `0`, field `5`" — so a real topic named `0` still wins.
 */
export function JointBindings({
  joints,
  entityId,
  mesh,
  panelId,
}: JointBindingsProps) {
  const dispatch = useVizDispatch();
  const availableFiles = useDataFiles();
  const fileIdToTopics = useFileIdToTopics();

  const jointValues = mesh.jointValues;

  // Which joints have no computable value at all — a coupling cycle, a driver
  // the model does not contain, a name another joint already took. Computed
  // here rather than per row, since it is a property of the whole list.
  const unresolvable = React.useMemo(
    () => mimicOrder(joints).unresolvable,
    [joints],
  );

  // The joints that get a row: the ones a value can be given to, plus any the
  // model is already being posed by. See the note above.
  const listed = React.useMemo(
    () =>
      joints.filter(
        (joint) =>
          jointControlState(joint, unresolvable).bindable ||
          jointValues?.[joint.name] !== undefined,
      ),
    [joints, jointValues, unresolvable],
  );

  // The file a joint is already bound to wins; otherwise the user's last pick;
  // otherwise the only file there is, since `FileSelector` hides itself when
  // there is one and the paths would have nothing to offer.
  const boundFileId = React.useMemo(() => {
    for (const binding of Object.values(jointValues ?? {})) {
      if (typeof binding.value !== "number") {
        return binding.value.topic.association.association_id;
      }
    }
    return undefined;
  }, [jointValues]);

  const [pickedFile, setPickedFile] = React.useState<string | undefined>(
    undefined,
  );
  const effectiveFileId =
    boundFileId ??
    pickedFile ??
    (availableFiles.length === 1 ? availableFiles[0].file_id : undefined);
  const topicTree = effectiveFileId
    ? (fileIdToTopics[effectiveFileId] ?? [])
    : [];

  // A PathSelector reads its input once, on mount, so a change of file reaches
  // the rows only by remounting them.
  const reloadKey = effectiveFileId ?? "none";

  const bind = React.useCallback(
    (joint: Joint, value: TopicData | number) => {
      // The scale is the only thing standing between a topic publishing degrees
      // and a robot that spins fourteen turns past its limits: nothing clamps a
      // joint driven by recorded data, on purpose — `drivenValues` holds
      // *authored* values inside the joint's travel and lets observations
      // through as recorded. A constant is exempt from the scale — it was typed
      // in the joint's own units already, and is authored, so it is the value
      // that gets held inside the limits. See `jointScaleFor`.
      const binding: JointValue = { value, scale: jointScaleFor(value) };
      dispatch(
        updateThreeDEntity(panelId, entityId, {
          mesh: {
            ...mesh,
            jointValues: { ...mesh.jointValues, [joint.name]: binding },
          },
        }),
      );
    },
    [dispatch, entityId, mesh, panelId],
  );

  if (joints.length === 0) {
    return null;
  }

  // The count names what is on screen. A model whose joints are all inert still
  // says so in a line rather than showing an empty labelled box — "no joints
  // here" and "this section is broken" must not look the same.
  if (listed.length === 0) {
    return (
      <>
        <RobotoTypography variant="body2" className={styles.fieldLabel}>
          Bindable Joints (0)
        </RobotoTypography>
        <div className={styles.container}>
          <RobotoTypography variant="caption">
            No joints on this model can be driven by data
          </RobotoTypography>
        </div>
      </>
    );
  }

  return (
    <>
      <RobotoTypography variant="body2" className={styles.fieldLabel}>
        {`Bindable Joints (${listed.length})`}
      </RobotoTypography>
      <div className={styles.container}>
        <ConfigItem label="File">
          <FileSelector
            files={availableFiles}
            selection={effectiveFileId}
            onSelect={setPickedFile}
          />
        </ConfigItem>

        <div key={reloadKey}>
          {listed.map((joint) => {
            const control = jointControlState(joint, unresolvable);
            const limits = limitsLabel(joint);
            const label = limits ? `${joint.name} (${limits})` : joint.name;

            if (!control.bindable) {
              // Listed at all only because it carries a value — see the note on
              // this component. Said outright, because the joint's own state
              // ("Fixed", "Pinned by its limits") reads as "so nothing is
              // happening here", and something is.
              const held = jointValues?.[joint.name];
              return (
                <ConfigItem key={joint.name} label={label}>
                  <RobotoTypography variant="caption">
                    {control.reason}
                  </RobotoTypography>
                  {held !== undefined && (
                    <RobotoTypography
                      className={styles.jointNote}
                      variant="caption"
                    >
                      {`Still driven by ${jointValueText(held.value) ?? "an unreadable value"}`}
                    </RobotoTypography>
                  )}
                </ConfigItem>
              );
            }
            const note = jointValueNote(joint, jointValues?.[joint.name]);
            return (
              <ConfigItem key={joint.name} label={label}>
                <PathSelector
                  initialInputValue={jointValueText(
                    jointValues?.[joint.name]?.value,
                  )}
                  isValidFreeText={isConstant}
                  onChange={(node) => bind(joint, node.toTopicData())}
                  // Clearing pins the joint at zero rather than deleting the
                  // key, and the difference matters: `withDefaultBindings` reads
                  // an absent key as "nobody has decided about this joint" and
                  // re-applies the model's declared default over it on the next
                  // import, so deleting would resurrect what was just removed.
                  //
                  // The ingest wizard's identical-looking button means the
                  // opposite, and deliberately: that one authors a *declaration*,
                  // where absence is simply no declaration, while this one binds
                  // a *value*, where zero is a legitimate pose.
                  onClear={() => bind(joint, 0)}
                  onFreeText={(text) => {
                    const constant = jointConstantFor(text);
                    if (constant !== undefined) {
                      bind(joint, constant);
                    }
                  }}
                  options={topicTree}
                  optionFilter={isViewableAsPlot}
                />
                {/* Which of the two happened — a conversion the catalogue
                    justified, or the SI assumption made in the absence of one.
                    Stated, because a unit is invisible in a path and a joint fed
                    degrees as radians spins rather than fails. */}
                {note !== undefined && (
                  <RobotoTypography
                    className={styles.jointNote}
                    variant="caption"
                  >
                    {note}
                  </RobotoTypography>
                )}
              </ConfigItem>
            );
          })}
        </div>
      </div>
    </>
  );
}
